import { FC, memo, useCallback, useMemo } from 'react';
import { TaskCenterTypes } from '@frontend/api-task-center';
import { PaginatedList } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { useTaskCenterStore } from '@frontend/task-center-scope';
import { TaskCenterTabs } from '../constants';
import { useGetTasksV2 } from '../hooks';
import { taskCenterTrackingIds } from '../tracking-ids';
import { getTaskEmptyState } from '../utils';
import { TaskItem } from './common';

interface TaskByStatusProps {
  status: TaskCenterTypes.TaskStatus;
}

export const TaskByStatus: FC<TaskByStatusProps> = memo(({ status }) => {
  const { t } = useTranslation('task-center');
  const { setTaskMode, selectedTab } = useTaskCenterStore(['setTaskMode', 'selectedTab']);
  const currentTab = useMemo(() => TaskCenterTabs.find((tab) => tab.controller === selectedTab), [selectedTab]);
  const {
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    status: queryStatus,
    data,
    isFetching,
  } = useGetTasksV2({
    status,
    enabled: currentTab?.status === status,
  });
  const list = useMemo(() => {
    return data ? data.pages.flatMap((page) => page.tasks).filter((task) => !!task) : [];
  }, [data]);

  const openCreateTask = useCallback(() => setTaskMode('create'), [setTaskMode]);

  const emptyStateConfig = {
    type: getTaskEmptyState(status),
    ...(status !== TaskCenterTypes.TaskStatus.STATUS_COMPLETED && {
      action: {
        label: t('Create Task'),
        onClick: openCreateTask,
        trackingId: taskCenterTrackingIds.createTaskButton,
        'aria-label': t('Create Task'),
      },
    }),
    header: t('No Tasks Found'),
    description: t('Try adjusting filters, check other tabs, or create a new task to get started.'),
  };

  return (
    <PaginatedList
      listData={list}
      renderListItem={({ listItem }) => <TaskItem key={listItem.id} {...listItem} />}
      hasNextPage={hasNextPage}
      isError={queryStatus === 'error'}
      isLoading={queryStatus === 'loading' || isFetching}
      isFetchingNextPage={isFetchingNextPage}
      overscanCount={20}
      loadMore={fetchNextPage}
      emptyStateConfig={emptyStateConfig}
      endOfListText={t('You have reached the end of the list 🎉')}
      height='100%'
    />
  );
});

TaskByStatus.displayName = 'TaskByStatus';
